import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Printer, ArrowLeft } from 'lucide-react'
import { getMyOrders, type Order } from '@/services/orderService'
import ReceiptCard from './ReceiptCard'

export default function ReceiptView() {
  const { orderId } = useParams<{ orderId: string }>()
  const [order, setOrder] = useState<Order | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // บังคับขนาดกระดาษ A4 แนวตั้งเฉพาะหน้านี้ — ลองใช้ CSS named page (@page receipt / page: receipt) ใน index.css
  // มาก่อนแต่เบราว์เซอร์ไม่รองรับเสถียรพอ (พิมพ์ผิดขนาด/แนวกระดาษ) เปลี่ยนมาฉีด <style> ที่มาทีหลังสุดใน <head>
  // แทน ซึ่งชนะกฎ @page เดิม (297mm×210mm แนวนอนของใบประกาศ) ได้แน่นอนเพราะโหลดทีหลัง แล้วลบออกตอนออกจากหน้านี้
  useEffect(() => {
    const style = document.createElement('style')
    style.textContent = '@media print { @page { size: 210mm 297mm; margin: 0; } }'
    document.head.appendChild(style)
    return () => { document.head.removeChild(style) }
  }, [])

  useEffect(() => {
    if (!orderId) return
    // ยังไม่มี endpoint ดึงคำสั่งซื้อทีละใบ — โหลดรายการทั้งหมดแล้วหาใบที่ตรง id (billing_info มากับ order นี้แล้ว)
    getMyOrders()
      .then((orders) => {
        const found = orders.find((o) => o.order_id === Number(orderId))
        if (!found) throw new Error('ไม่พบคำสั่งซื้อนี้')
        setOrder(found)
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'โหลดคำสั่งซื้อไม่สำเร็จ'))
      .finally(() => setLoading(false))
  }, [orderId])

  return (
    <div className="min-h-screen bg-[#FAFAFB] pt-24 pb-16">
      <div className="mx-auto max-w-4xl px-4 md:px-6">
        <div className="mb-6 flex items-center justify-between print:hidden">
          <Link to="/account?tab=orders" className="flex items-center gap-1.5 text-sm text-text-muted hover:text-accent">
            <ArrowLeft className="h-4 w-4" />
            กลับไปประวัติคำสั่งซื้อ
          </Link>
          {order && (
            <button
              onClick={() => window.print()}
              className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-accent to-accent2 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-accent/25 hover:shadow-accent/40 transition-all"
            >
              <Printer className="h-4 w-4" />
              ดาวน์โหลด PDF
            </button>
          )}
        </div>

        {order && !order.billing_info && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-700 print:hidden">
            คำสั่งซื้อนี้ไม่ได้ขอใบกำกับภาษี/ใบเสร็จตอนชำระเงิน
          </div>
        )}

        {loading ? (
          <p className="text-sm text-text-muted">กำลังโหลด...</p>
        ) : error ? (
          <p className="text-sm text-rose-500">{error}</p>
        ) : order ? (
          <ReceiptCard order={order} />
        ) : null}
      </div>
    </div>
  )
}
